import { Injectable } from '@angular/core';

import { DefaultStandWorksheetCalcService } from './stand-worksheet-calc.service.default';
import {
  IInformationData,
  IStandWorkSheetItemFullData,
} from '@app/shared/types/entities/stand-worksheet';
import { DecimalNumber } from '@app/shared/common';
import { GridRowData, IBaseEntityType } from '@app/shared/types';
import { reduce } from 'lodash';
import { CALCULATION_VERSIONS } from './calculation.enums';
import { STAND_WORKSHEET_HEADER_INFO_MESSAGE_V2 } from '../stand-worksheet-info-message';

@Injectable()
export class StandWorksheetCalcServiceV2 extends DefaultStandWorksheetCalcService {
  public static VERSION: string = CALCULATION_VERSIONS.V2;

  public calcInfoReceiptTotal(
    informationData: IInformationData,
  ): DecimalNumber {
    // Receipt Total = cash_count + cash + cash_post_closure
    // + (credit + credit_post_closure + debit+ debit_post_closure + gift + gift_post_closure)
    // The fields in the top row are already calculated
    // in credit_debit_gift_total and credit_debit_gift_post_closure_total
    // + custom + custom_post_closure - tips (Tips) - tips_post_closure(Tips) - Fees/Other
    return new DecimalNumber(informationData.cash || 0)
      .add(informationData.cash_count || 0)
      .add(
        this.getPostClosureValueWithPreference(
          informationData.cash_post_closure,
        ),
      )
      .add(informationData.credit_debit_gift_total || 0)
      .add(
        this.getPostClosureValueWithPreference(
          informationData.credit_debit_gift_post_closure_total,
        ),
      )
      .add(informationData.custom || 0)
      .add(
        this.getPostClosureValueWithPreference(
          informationData.custom_post_closure,
        ),
      )
      .sub(informationData.tips || 0)
      .sub(
        this.getPostClosureValueWithPreference(
          informationData.tips_post_closure,
        ),
      )
      .sub(informationData.miscellaneous || 0);
  }

  public calcInformationData(
    data: GridRowData[],
    currentDocument: IBaseEntityType,
  ): IInformationData {
    // get the not linked recipe sale and discount
    const initialData = {
      physical_sales: new DecimalNumber(
        currentDocument?.unlinked_recipe_tax || 0,
      )
        .add(
          this.getPostClosureValueWithPreference(
            currentDocument?.unlinked_recipe_tax_post_closure,
          ),
        )
        .sub(currentDocument?.unlinked_recipe_discount || 0)
        .sub(
          this.getPostClosureValueWithPreference(
            currentDocument?.unlinked_recipe_discount_post_closure,
          ),
        ),
      terminal_sales: new DecimalNumber(
        currentDocument?.unlinked_recipe_price || 0,
      ),
      post_closure_sales: new DecimalNumber(
        currentDocument?.unlinked_recipe_price_post_closure || 0,
      ),
      total_dollars_discounted: new DecimalNumber(
        currentDocument?.unlinked_recipe_discount || 0,
      ),
      post_closure_dollars_discounted: new DecimalNumber(
        currentDocument?.unlinked_recipe_discount_post_closure || 0,
      ),
      total_tax: new DecimalNumber(currentDocument?.unlinked_recipe_tax || 0),
      total_tax_post_closure: new DecimalNumber(
        currentDocument?.unlinked_recipe_tax_post_closure || 0,
      ),
    } as IInformationData;

    return reduce(
      data,
      (sum, cur) => {
        sum.physical_sales = sum.physical_sales.add(
          cur.physical_dollars ? cur.physical_dollars : 0,
        );
        sum.terminal_sales = sum.terminal_sales.add(cur.price ? cur.price : 0);
        sum.post_closure_sales = sum.post_closure_sales.add(
          cur.price_post_closure ? cur.price_post_closure : 0,
        );
        sum.post_closure_dollars_discounted =
          sum.post_closure_dollars_discounted.add(
            cur.discount_post_closure ? cur.discount_post_closure : 0,
          );
        sum.total_dollars_discounted = sum.total_dollars_discounted.add(
          cur.discount ? cur.discount : 0,
        );
        sum.total_tax = sum.total_tax.add(cur.tax || 0);
        sum.total_tax_post_closure = sum.total_tax_post_closure.add(
          cur.tax_post_closure || 0,
        );

        return sum;
      },
      initialData,
    );
  }

  public calculatePriceAdjustmentColumn(
    item: IStandWorkSheetItemFullData,
  ): DecimalNumber {
    // Terminal Dollars + Post_Closure Dollars
    // + Discounts + Post-Closure Discounts
    // + refund + Post-Closure Refund
    // + refund_discount + refund_discount_post_closure
    // - tax - tax_post_closure
    // - Parent Product Price * Qty that all linked products sold
    const productCount = new DecimalNumber(item.product_count || 0).add(
      this.getPostClosureValueWithPreference(item.product_count_post_closure),
    );
    const parentProductDollars = new DecimalNumber(
      item.parent_product_price || 0,
    ).mul(productCount || 0);

    return new DecimalNumber(item.price || 0)
      .add(this.getPostClosureValueWithPreference(item.price_post_closure))
      .add(item.refund || 0)
      .add(item.refund_discount || 0)
      .add(this.getPostClosureValueWithPreference(item.refund_post_closure))
      .add(
        this.getPostClosureValueWithPreference(
          item.refund_discount_post_closure,
        ),
      )
      .add(item.discount || 0)
      .add(this.getPostClosureValueWithPreference(item.discount_post_closure))
      .sub(item.tax || 0)
      .sub(this.getPostClosureValueWithPreference(item.tax_post_closure))
      .sub(parentProductDollars);
  }

  public getOverrideToolTips(): Record<string, string> {
    return STAND_WORKSHEET_HEADER_INFO_MESSAGE_V2;
  }
}
